<template>
  <div>
    <empty-resource-table v-if="!workExperiences[0] && !loadingTable" resource="work-experiences" />

    <a-table
      v-if="loadingTable || workExperiences[0]"
      :columns="workExperiencesColumns"
      :data-source="workExperiences"
      :loading="loadingTable"
      :pagination="false"
      :expand-row-by-click="true"
      :scroll="{ x: 1000 }"
      :row-key="(we) => we.id"
      @change="(pagination, filters, sorter) => handleChange(pagination, filters, sorter)"
    >
      <template #user="name, we">
        <span class="d-flex align-items-center">
          <a-avatar :src="we.user.gravatar" class="min-width-avatar mr-2" />
          <router-link :to="{ name: 'user', params: { id: we.user.id } }" @click.native.stop>
            {{ name }}
          </router-link>
        </span>
      </template>

      <template #company="we">
        <span class="d-flex flex-column">
          <small>{{ we.company_name }}</small>
        </span>
      </template>

      <template #expandedRowRender="we">
        <div>
          <a-row>
            <a-col :span="8">
              <strong class="mr-2">Title:</strong>
              {{ we.title }}
            </a-col>

            <a-col :span="8">
              <strong class="mr-2">Start date:</strong>
              {{ we.start_date }}
            </a-col>
          </a-row>

          <a-row>
            <a-col :span="8">
              <strong class="mr-2">Company:</strong>
              {{ we.company_name }}
            </a-col>

            <a-col :span="8">
              <strong class="mr-2">End date:</strong>
              {{ we.end_date }}
            </a-col>
          </a-row>

          <a-row class="mt-2">
            <strong class="mr-2">Description:</strong>
            <span>
              {{ we.description }}
            </span>
          </a-row>

          <a-row v-if="we.skills && we.skills[0]" class="mt-2">
            <a-col :span="22">
              <strong class="mr-2">Skills:</strong>
              <span v-for="skill in we.skills" :key="skill.id">
                <a-tag class="default-cursor">{{ skill.title }}</a-tag>
              </span>
            </a-col>
          </a-row>

          <a-row v-if="we.roles && we.roles[0]" class="mt-2">
            <a-col :span="22">
              <strong class="mr-2">Associated roles:</strong>
              <span v-for="role in we.roles" :key="role.id">
                <a-tag class="default-cursor" @click.stop="redirectToRoles(role)">
                  {{ role.title }}
                </a-tag>
              </span>
            </a-col>
          </a-row>

          <a-row>
            <a-col :span="20">
              <work-experience-actions
                :work-experience="we"
                :profile="profile"
                :acl="acl"
                :with-title="true"
                @show-edit-work-experience-modal="() => showEditWorkExperienceModal(we)"
                @show-delete-work-experience-modal="() => showDeleteWorkExperienceModal(we)"
              />
            </a-col>
          </a-row>
        </div>
      </template>
    </a-table>

    <a-pagination
      v-if="workExperiencesPagination && (loadingTable || workExperiences[0])"
      :current="currentPage"
      :total="workExperiencesPagination.total"
      :show-size-changer="true"
      :page-size="pageSize"
      :page-size-options="['10', '25', '50', '100']"
      size="small"
      class="d-flex justify-content-end mt-2"
      @change="
        (page, newPageSize) => $emit('get-work-experiences-page', { page, pageSize: newPageSize })
      "
      @showSizeChange="
        (pageNumber, newPageSize) =>
          $emit('get-work-experiences-page', { page: pageNumber, pageSize: newPageSize })
      "
    />

    <!-- EDIT WORK EXPERIENCE MODAL -->
    <edit-work-experience-modal
      :work-experience="workExperience"
      :visible="editWorkExperienceModalVisible"
      :profile="profile"
      :skills="skills"
      :roles="roles"
      @close="editWorkExperienceModalVisible = false"
      @update-work-experience="
        (updatedWorkExperience) => $emit('update-work-experience', updatedWorkExperience)
      "
    />

    <!-- DELETE WORK EXPERIENCE MODAL -->
    <delete-work-experience-modal
      :visible="deleteWorkExperienceModalVisible"
      :work-experience="workExperience"
      @close="deleteWorkExperienceModalVisible = false"
      @delete-work-experience="
        (workExperienceId) => $emit('delete-work-experience', workExperienceId)
      "
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { WorkExperienceInterface, WorkExperiencesFilterInterface } from "../types";
import { PaginationInterface, TableColumnInterface } from "@/common/types";
import { BaseAclInterface, ProfileInterface } from "@/modules/authentication/types";
import { workExperiencesColumns as defaultWorkExperiencesColumns } from "../_utils/tables";
import EmptyResourceTable from "@/modules/common/_components/EmptyResourceTable.vue";
import WorkExperienceActions from "./WorkExperienceActions.vue";
import EditWorkExperienceModal from "./EditWorkExperienceModal.vue";
import DeleteWorkExperienceModal from "./DeleteWorkExperienceModal.vue";
import { SkillInterface } from "@/modules/consultant_profiles/types";
import { RoleSmallInterface } from "@/modules/roles/types";

// Props
const props = defineProps({
  workExperiencesPagination: { type: Object as () => PaginationInterface, default: undefined },
  profile: { type: Object as () => ProfileInterface, default: undefined },
  workExperiences: { type: Array as () => Array<WorkExperienceInterface>, default: () => [] },
  skills: { type: Array as () => Array<SkillInterface>, default: () => [] },
  roles: { type: Array as () => Array<RoleSmallInterface>, default: () => [] },
  acl: { type: Object as () => BaseAclInterface, default: undefined },
  loadingTable: { type: Boolean, default: false },
  currentPage: { type: Number, default: 1 },
  pageSize: { type: Number, default: 25 },
  workExperiencesColumns: {
    type: Array as () => Array<TableColumnInterface>,
    default: () => defaultWorkExperiencesColumns,
  },
});

// Emits
const emits = defineEmits([
  "apply-sorting",
  "update-work-experience",
  "delete-work-experience",
  "get-skills",
]);

// Data properties
const workExperience = ref<WorkExperienceInterface>(undefined);
const editWorkExperienceModalVisible = ref<boolean>(false);
const deleteWorkExperienceModalVisible = ref<boolean>(false);

// Class methods
const handleChange = (
  pagination: PaginationInterface,
  filters: WorkExperiencesFilterInterface,
  sorter: any
): void => {
  emits("apply-sorting", {
    sort: sorter.columnKey,
    order: sorter.columnKey ? (sorter.order === "descend" ? "desc" : "asc") : undefined,
  });
};

const redirectToRoles = (role: any): void => {
  window.location.href = `/dashboard/roles?filters[role_id]=${role.id}`;
};

const showEditWorkExperienceModal = (we: WorkExperienceInterface): void => {
  workExperience.value = we;
  editWorkExperienceModalVisible.value = true;
};

const showDeleteWorkExperienceModal = (we: WorkExperienceInterface): void => {
  workExperience.value = we;
  deleteWorkExperienceModalVisible.value = true;
};
</script>

<style scoped>
.min-width-avatar {
  min-width: 32px;
}
</style>
